import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import { Digest } from '../../global/digest';
import {
    formatMonthsValue,
    formatStep,
    FormatterType,
    formatYearsValue,
} from '../../global/formatters';
import { keyCodes, TabIndex } from '../../global/keyboard';
import { Labels } from '../../global/labels';
import {
    createElement,
    dispatchCustomEvent,
    dispatchNativeEvent,
    getCSSPropertyValue,
    insertElements,
    setInnerText,
    setNativeInputValue,
    updateClassList,
    updateElement,
} from '../../global/ui-helpers';
import type { TranslationLabels } from '../../types/labels.type';
import { UIField } from '../field/ui-field';
import { UIElement } from '../ui-element';
import type { AttributeValue, ElementConfig } from '../ui-element.type';
import styles from './ui-slider.css';
import type { UISliderEvents } from './ui-slider.type';
import { runInNextFrame } from '../../global/helpers';

/**
 * @class
 * @memberof SharedComponents
 * @element ui-slider
 * @augments {UIElement}
 * @alias UISlider
 * @element ui-slider
 * @classdesc Represents a class for <code>ui-slider</code> element.
 * @property {boolean} [editable] {@attr editable} Slider is editable or disabled.
 * @property {boolean} [unlimited] {@attr unlimited} Allow put value grater than the max one.
 * @property {boolean} [disableTooltip] {@attr disable-tooltip} Disables the tooltip showing the slider value.
 * @property {boolean} [enableInputUnits] {@attr enable-input-units} Enables the units display in the input.
 * @property {boolean} [fullWidth] {@attr full-width} Renders the slider as full-width.
 * @property {FormatterType} [formatter] {@attr formatter}
 * Function to format slider value.
 * @property {string} [controlId] {@attr control-id} Base id override for the slider (legacy).
 * Preferred way: set the id directly on the slotted native input element.
 * When neither slotted input id nor control-id are provided, a random id is generated automatically.
 * @property {string} [units] {@attr units} Shows unit as a suffix for the values.
 * @property {HTMLInputElement} control Main input for storing model value and
 * handle events.
 * @property {number} min The minimum slider's value (proxies from control).
 * @property {number} max The maximum slider's value (proxies from control).
 * @property {number} maxlength The maximum length of the slider value (proxies from control).
 * @property {number} step The step value of a slider (proxies from control).
 * @property {number} value The value of the slider (proxies from control).
 * @property {HTMLInputElement} input Additional input for mobile view.
 * @property {HTMLDivElement} sliderValueContainer Shortcut to slider value container div element.
 * @property {HTMLDivElement} sliderValue Shortcut to slider value div element (contains only a text node).
 * @property {HTMLElement|UIHint} sliderMin Shortcut to label min container.
 * @property {HTMLElement|UIHint} sliderMax Shortcut to label max container.
 * @property {HTMLDivElement} container Shortcut to container contains whole slider bar.
 * @property {HTMLDivElement} progressBar Shortcut to progress bar.
 * @property {HTMLDivElement} handle Shortcut to slider handler.
 * @property {HTMLDivElement} track Shortcut to slider track.
 * @property {HTMLDivElement} bar Shortcut to slider bar.
 * @property {HTMLDivElement} field Shortcut to input field.
 * @slot
 * @example
 * <ui-slider units="€">
 *   <input type="range" min="5000" max="50000" step="1" value="28000" maxlength="5"/>
 * </ui-slider>
 */
@Component({ tag: 'ui-slider', styles })
export class UISlider extends UIElement {
    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-slider', {
            month: {
                1: 'month',
                2: 'months',
            },
            year: {
                1: 'year',
                2: 'years',
            },
            value: 'Value',
            minValue: 'Minimum value is',
            maxValue: 'Maximum value is',
        });
    }

    @Attribute(Boolean) editable?: boolean;
    @Attribute(Boolean) unlimited?: boolean;
    @Attribute(String) formatter?: string;
    @Attribute(String) units: string = '';
    @Attribute(String) controlId?: string;
    @Attribute(Boolean) disableTooltip?: boolean;
    @Attribute(Boolean) fullWidth?: boolean;
    @Attribute(Boolean) enableInputUnits?: boolean;

    @Child('.ui-slider__control') control!: HTMLInputElement;
    @Child('.ui-slider__input') input!: HTMLInputElement;
    @Child('.ui-slider__container') container!: HTMLDivElement;
    @Child('.ui-slider__value-container') sliderValueContainer!: HTMLDivElement;
    @Child('.ui-slider__value') sliderValue!: HTMLDivElement;
    @Child('.ui-slider__track') track!: HTMLDivElement;
    @Child('.ui-slider__progress') progressBar!: HTMLDivElement;
    @Child('.ui-slider__handle') handle!: HTMLElement;
    @Child('.ui-slider__bar') bar!: HTMLDivElement;
    @Child('.ui-slider__field') field!: HTMLDivElement;
    @Child('.ui-slider__limit.-min') sliderMin!: HTMLElement;
    @Child('.ui-slider__limit.-max') sliderMax!: HTMLElement;
    @Child('.ui-slider__limits') sliderLimits!: HTMLDivElement;

    private _id?: string;
    private _hasUserSetNativeId: boolean = false;
    private observer!: IntersectionObserver;
    private controlObserver!: MutationObserver;

    get min(): number {
        return Number(this.control?.min) || 0;
    }

    set min(value: number | string) {
        if (this.control) {
            this.control.min = String(value);
        }
    }

    get max(): number {
        return Number(this.control?.max) || 100;
    }

    set max(value: number | string) {
        if (this.control) {
            this.control.max = String(value);
        }
    }

    get step(): number {
        return Number(this.control?.step) || 1;
    }

    set step(value: number | string) {
        if (this.control) {
            this.control.step = String(value);
        }
    }

    get value(): number {
        return Number(this.control?.value) || 0;
    }

    set value(value: number | string) {
        if (this.control) {
            setNativeInputValue(this.control, String(value));
        }
    }

    get maxlength(): number {
        return this.control.maxLength;
    }

    set maxlength(value: number) {
        this.control.maxLength = value;
    }

    /**
     * Stable generated id when neither slotted input id nor control-id is set.
     * @private
     */
    private get uid(): string {
        return (this._id = this._id || Digest.randomId());
    }

    /**
     * @type {boolean}
     */
    get interact(): boolean {
        return this.classList.contains('-interact');
    }

    static get events(): UISliderEvents {
        return {
            start: ['mousedown', 'touchstart', 'pointerdown'],
            move: ['mousemove', 'touchmove', 'pointermove'],
            end: ['mouseup', 'touchend', 'pointerup'],
        };
    }

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): Array<string> {
        return ['control-id'];
    }

    /**
     * Formats value based on given formatter.
     * @param {number} value
     * @param {boolean} [isInput]
     * @param {boolean} [noUnits]
     * @returns {string}
     */
    formatValue(
        value: number,
        isInput: boolean = false,
        noUnits: boolean = false
    ): string {
        switch (this.formatter) {
            case FormatterType.Years:
                return formatYearsValue(value, {
                    noUnits,
                    labels: UISlider.labels,
                });
            case FormatterType.Months:
                return formatMonthsValue(value, {
                    noUnits,
                    labels: UISlider.labels,
                });
            case FormatterType.String:
                return String(value);
            default: {
                const parts: Array<string | number> = [value];
                if (!noUnits) {
                    parts.push(this.units);
                }
                return parts.join(' ').trim();
            }
        }
    }

    /**
     * Calculates discrete value based on the given relative value and the configured step.
     * @param {number} valueRelative
     * @returns {number}
     */
    calcDiscreteValue(valueRelative: number): number {
        const scale = (this.max - this.min) / this.step;
        const index = Math.round(scale * valueRelative);
        return formatStep(index * this.step + this.min, { step: this.step });
    }

    /**
     * Calculates progress value based on the given absolute value.
     * @param {number} value
     * @returns {number}
     */
    calcProgress(value: number): number {
        if (this.unlimited && this.max && value >= this.max) {
            return 100;
        }
        return ((value - this.min) / (this.max - this.min)) * 100;
    }

    /**
     * Sets the min and max values to the hint placeholders.
     * @returns {UISlider}
     */
    updateHints(): UISlider {
        setInnerText(this.sliderMin, this.formatValue(this.min, false));
        setInnerText(this.sliderMax, this.formatValue(this.max, false));
        return this;
    }

    /**
     * Sets an absolute value to the controls
     * @param {number} value
     * @returns {UISlider}
     */
    setValue(value: number): UISlider {
        const isDateSlider = this.formatter;
        this.value = this.alignValueToRange(value);
        this.updateInputValue(value);
        setInnerText(
            this.sliderValue,
            this.formatValue(this.value, false, !isDateSlider)
        );
        dispatchNativeEvent(this.control, 'input', true);
        dispatchNativeEvent(this.control, 'change', true);
        this.handle.setAttribute('aria-valuemin', String(this.min));
        this.handle.setAttribute('aria-valuemax', String(this.max));
        this.handle.setAttribute('aria-valuenow', String(this.value));
        this.handle.setAttribute(
            'aria-valuetext',
            this.formatValue(this.value)
        );
        this.updateSliderAccessibility();
        return this;
    }

    /**
     * Updates the text input field's value.
     * @param {number} v - The value to update the input field with.
     * @private
     */
    private updateInputValue(v: number): void {
        this.input.value = this.formatValue(v, true, true);
    }

    /**
     * Checks if value is in range and return an edge value if the value is out of the range.
     * @param {number} value
     * @returns {number}
     */
    alignValueToRange(value: number): number {
        if (value > this.max && !this.unlimited) {
            return this.max;
        }
        if (value < this.min) {
            return this.min;
        }
        return value;
    }

    /**
     * Sets offsets for dynamic elements based on given progress.
     * @param {number} progress
     * @returns {UISlider}
     */
    setSliderPosition(progress: number): UISlider {
        this.adjustElementPosition(this.handle, progress);
        this.adjustElementPosition(this.sliderValueContainer, progress);
        const x = ((100 - progress) * -1).toFixed(2) + '%';
        this.style.setProperty('--slider-progress-x', x);
        return this;
    }

    /**
     * Sets offsets for the give element based on given progress and bounding box.
     * @param {HTMLElement} element
     * @param {number} progress
     * @returns {UISlider}
     */
    adjustElementPosition(element: HTMLElement, progress: number): UISlider {
        const x = this.calcElementPosition(element, progress) + 'px';
        element.style.setProperty('--slider-handle-x', x);
        return this;
    }

    /**
     * Calc position of the element based on given progress and bounding box.
     * @param {HTMLElement} element
     * @param {number} progress
     * @returns {number}
     */
    private calcElementPosition(
        element: HTMLElement,
        progress: number
    ): number {
        const trackRect = this.track.getBoundingClientRect();
        const offsetWidth = element.offsetWidth;

        // Value container should be centered on the handle's center point
        // but constrained to stay within track boundaries
        if (element === this.sliderValueContainer) {
            const handleWidth = this.handle.offsetWidth;
            const handleAvailable = trackRect.width - handleWidth;
            const handlePosition = (progress / 100) * handleAvailable;
            const handleCenter = handlePosition + handleWidth / 2;
            const centeredPosition = handleCenter - offsetWidth / 2;
            const maxPosition = trackRect.width - offsetWidth;
            return Math.max(0, Math.min(centeredPosition, maxPosition));
        }

        // Handle uses boundary-constrained positioning
        const available = trackRect.width - offsetWidth;
        const nextPosition = (progress / 100) * available;
        return Math.max(0, Math.min(nextPosition, available));
    }

    /**
     * Sets offset for slider based on current input value.
     * @returns {UISlider}
     */
    adjustSliderPosition(): UISlider {
        this.setSliderPosition(this.calcProgress(this.value));
        return this;
    }

    /**
     * Triggers input validation by dispatching validation events
     * @private
     */
    private triggerInputValidation(): void {
        this.updateInputValue(this.value);
        dispatchNativeEvent(this.input, 'input', true);
        dispatchNativeEvent(this.input, 'change', true);
    }

    /**
     * Applies the current input value to user controls.
     * @returns {UISlider}
     */
    updateValue(): UISlider {
        this.setValue(this.value);
        this.setSliderPosition(this.calcProgress(this.value));
        this.updateAriaValueTextWithCurrentOnly();
        return this;
    }

    /**
     * Sync maxlength property with input
     * @private
     */
    private updateMaxlength(): void {
        updateElement(this.input, {
            attributes: {
                maxlength: this.maxlength >= 0 ? this.maxlength : null,
            },
        });
    }

    /**
     * @private
     */
    private setUnitsInputsSize(): void {
        const fontSize = getCSSPropertyValue(
            'font-size',
            false,
            this.input
        ) as number;
        const defaultWidth = 100;
        this.field.style.setProperty(
            '--input-size',
            `${Math.max(this.input.value.length * fontSize, defaultWidth)}px`
        );
    }

    /**
     * Sync progress bar appearance
     */
    sync(): void {
        updateClassList(this, { '-interact': true });
        runInNextFrame(() => {
            this.setSliderPosition(this.calcProgress(this.value));
            updateClassList(this, { '-interact': false });
            dispatchCustomEvent(this, 'sync', {}, true);
        });
    }

    /**
     * Handles attributes mutation.
     * @param {MutationRecord} mutation
     * @private
     */
    private handleAttributesMutation(mutation: MutationRecord): void {
        if (mutation.attributeName === 'maxlength') {
            this.updateMaxlength();
        }
    }

    /**
     * Fires callback when control mutates.
     * @param {Array<MutationRecord>} mutations
     * @private
     */
    private handleControlMutations(mutations: Array<MutationRecord>): void {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes') {
                this.handleAttributesMutation(mutation);
            }
        });
    }

    /**
     * Fires callback when an user clicks on the decrement button.
     * @private
     */
    private handleDecrementValue(): void {
        const newValue = formatStep(this.value - this.step, {
            step: this.step,
        });
        this.value = this.alignValueToRange(newValue);
        this.updateValue();
    }

    /**
     * Fires callback when an user clicks on the increment button.
     * @private
     */
    private handleIncrementValue(): void {
        const newValue = formatStep(this.value + this.step, {
            step: this.step,
        });
        this.value = this.alignValueToRange(newValue);
        this.updateValue();
    }

    /**
     * Fires callback when a user focuses the input element.
     * @param {Event} e
     * @private
     */
    private handleFocusInput(e: Event): void {
        const target = e.target as HTMLInputElement;
        target.type = 'number';
        target.value = String(this.value);
        this.updateAriaValueTextWithFullContext();
    }

    /**
     * Fires callback when a user blurs the input element.
     * @param {Event} e
     * @private
     */
    private handleBlurInput(e: Event): void {
        const target = e.target as HTMLInputElement;
        target.type = 'text';
        const value = this.alignValueToRange(Number(target.value));
        if (!this.unlimited || !this.max || value < this.max) {
            this.value = this.calcDiscreteValue(this.calcProgress(value) / 100);
        } else {
            this.value = value;
        }
        this.updateValue();
        this.setUnitsInputsSize();
        this.updateAriaValueTextWithFullContext();
    }

    /**
     * Fires callback when a user starts moving slider handle.
     * @param {PointerEvent | TouchEvent | MouseEvent} e
     * @private
     */
    private handleStartMove(e: Event): void {
        if ((e as MouseEvent).button === 2) {
            return;
        }

        // Move focus to the handle knob when mouse interaction starts. Remove focus
        // circle immediately as it is not needed for mouse actions.
        this.handle.focus();

        this.triggerInputValidation();

        updateClassList(this, { '-interact': true });
        UISlider.events.start.forEach((key) => {
            document.addEventListener(key, this.handleClickOutside, {
                passive: false,
            });
        });
        UISlider.events.end.forEach((key) => {
            document.addEventListener(key, this.handleEndMove, {
                passive: false,
            });
        });
        UISlider.events.move.forEach((key) => {
            document.addEventListener(key, this.handleDoMove, {
                passive: false,
            });
        });
        this.handleDoMove(e);
    }

    /**
     * Fires callback when a user is moving slider handle.
     * @param {PointerEvent | TouchEvent | MouseEvent} e
     * @private
     */
    private handleDoMove(
        e: PointerEvent | TouchEvent | MouseEvent | Event
    ): void {
        if (!this.interact) {
            return;
        }

        // This prevents default MacOS and iOS behaviour when drag slider window also slightly shifts.
        if (e.cancelable) {
            e.preventDefault();
        }

        let pageX: number;
        if ((e as TouchEvent).touches?.[0]) {
            pageX = (e as TouchEvent).touches[0].pageX;
        } else {
            e.preventDefault();
            pageX = (e as MouseEvent).pageX;
        }

        const trackRect = this.track.getBoundingClientRect();
        const handleWidth = this.handle.offsetWidth;

        // Adjust cursor position to account for handle centering
        const adjustedX = pageX - trackRect.left - handleWidth / 2;
        const availableWidth = trackRect.width - handleWidth;

        let valueRelative = adjustedX / availableWidth;
        if (valueRelative < 0) {
            valueRelative = 0;
        }
        if (valueRelative > 1) {
            valueRelative = 1;
        }

        const valueAbsolute = this.calcDiscreteValue(valueRelative);
        this.setSliderPosition(valueRelative * 100);
        this.setValue(valueAbsolute);
    }

    /**
     * Fires callback when an user stops moving slider handle.
     * @param {Event} e
     * @private
     */
    private handleEndMove(e: Event): void {
        updateClassList(this, { '-interact': false });
        UISlider.events.start.forEach((key) => {
            document.removeEventListener(key, this.handleClickOutside);
        });
        UISlider.events.end.forEach((key) => {
            document.removeEventListener(key, this.handleEndMove);
        });
        UISlider.events.move.forEach((key) => {
            document.removeEventListener(key, this.handleDoMove);
        });
        this.adjustSliderPosition();
    }

    /**
     * Fires callback when user clicked outside of slider element.
     * @param {Event} e
     * @private
     */
    private handleClickOutside(e: Event): void {
        if (this.interact && !this.isParentOf(e.target as Element)) {
            this.handleEndMove(e);
        }
    }

    /**
     * Fires callback when the handle get a key down event.
     * @param {Event} e
     * @private
     */
    private handleKeydown(e: Event): void {
        if (!(e instanceof KeyboardEvent)) {
            return;
        }

        switch (e.keyCode) {
            case keyCodes.LEFT:
                this.handleDecrementValue();
                e.preventDefault();
                break;
            case keyCodes.RIGHT:
                this.handleIncrementValue();
                e.preventDefault();
                break;
            case keyCodes.PAGEDOWN: {
                this.handleDecrementValue();
                this.updateValue();
                e.preventDefault();
                break;
            }
            case keyCodes.PAGEUP: {
                this.handleIncrementValue();
                this.updateValue();
                e.preventDefault();
                break;
            }
            case keyCodes.HOME:
                this.value = this.min;
                this.updateValue();
                e.preventDefault();
                break;
            case keyCodes.END:
                this.value = this.max;
                this.updateValue();
                e.preventDefault();
                break;
        }
    }

    /**
     * Fires callback when the window get resized event.
     * @private
     */
    private handleWindowResize(): void {
        this.sync();
        this.setValue(this.value);
    }

    /**
     * Fires callback when user inputs text.
     * @param {Event} e
     * @private
     */
    private handleInputValue(e?: Event): void {
        if (!e) {
            return;
        }
        const isDateSlider = this.formatter;
        const target = e.target as HTMLInputElement;
        const inputValue = Number(target.value) || this.min;
        let value: number;
        if (!this.unlimited || !this.max || inputValue <= this.max) {
            value = this.calcDiscreteValue(this.calcProgress(inputValue) / 100);
        } else {
            value = inputValue;
        }
        this.value = this.alignValueToRange(value);
        this.setSliderPosition(this.calcProgress(this.value));
        setInnerText(
            this.sliderValue,
            this.formatValue(this.value, false, !isDateSlider)
        );
        this.setUnitsInputsSize();
    }

    /**
     * Ensures the handle (role="slider") has an accessible name from the associated
     * label, the enclosing ui-field label, or a default. Called after render and from
     * updateSliderAccessibility
     * @private
     */
    private applyHandleAccessibleName(): void {
        const effectiveControlId = this.control?.id || this.controlId || null;
        if (effectiveControlId) {
            const labelForElem = document.querySelector(
                `label[for="${effectiveControlId}"]`
            );
            if (labelForElem) {
                labelForElem.id ||= `${effectiveControlId}-label`;
                this.handle.removeAttribute('aria-label');
                this.handle.setAttribute('aria-labelledby', labelForElem.id);
            }
        }

        if (
            !this.handle.getAttribute('aria-labelledby') &&
            !this.handle.getAttribute('aria-label')
        ) {
            const field = this.closest<UIField>('ui-field');
            const fieldLabelText =
                (field?.label as string)?.trim() ||
                field
                    ?.querySelector('.ui-field__label-inner')
                    ?.textContent?.trim() ||
                '';
            if (fieldLabelText) {
                this.handle.setAttribute('aria-label', fieldLabelText);
            } else {
                this.handle.setAttribute(
                    'aria-label',
                    UISlider.labels.value as string
                );
            }
        }
    }

    /**
     * Updates slider accessibility attributes including aria-label and input linking.
     * @private
     */
    private updateSliderAccessibility(): void {
        this.updateAriaValueTextWithFullContext();

        // ui-field automatically assigns ids to the slider min/max hints and links them via aria-describedby on the handle; the slider must strip those because they are already reported in the aria-valuetext.
        this.removeAriaDescribedByReferences([
            this.sliderMin?.id,
            this.sliderMax?.id,
        ]);
        this.applyHandleAccessibleName();

        if (this.sliderMin) {
            this.sliderMin.setAttribute('aria-hidden', 'true');
        }
        if (this.sliderMax) {
            this.sliderMax.setAttribute('aria-hidden', 'true');
        }
    }

    /**
     * Selectively removes aria-describedby references from the handle that match any of the provided ids.
     * @param {Array<string | undefined>} idsToRemove
     */
    private removeAriaDescribedByReferences(
        idsToRemove: Array<string | undefined>
    ): void {
        const ids = new Set(idsToRemove.filter((id): id is string => !!id));
        const describedBy = this.handle.getAttribute('aria-describedby');

        if (!ids.size || !describedBy) {
            return;
        }

        const remainingIds = describedBy
            .split(/\s+/)
            .filter((id) => id && !ids.has(id));

        if (remainingIds.length) {
            this.handle.setAttribute(
                'aria-describedby',
                remainingIds.join(' ')
            );
        } else {
            this.handle.removeAttribute('aria-describedby');
        }
    }

    /**
     * Updates the aria-valuetext attribute with only the current slider value.
     * Used during slider interactions to provide concise value announcements.
     * @private
     */
    private updateAriaValueTextWithCurrentOnly(): void {
        const current = this.formatValue(this.value);
        this.handle.setAttribute('aria-valuetext', current);
    }

    /**
     * Updates the aria-valuetext attribute with the current value and min/max limits.
     * Provides full context for screen readers including value range information.
     * @private
     */
    private updateAriaValueTextWithFullContext(): void {
        const current = this.formatValue(this.value);
        const limitsMinMaxLabel = `${UISlider.labels.minValue} ${this.min}, ${UISlider.labels.maxValue} ${this.max}`;
        this.handle.setAttribute(
            'aria-valuetext',
            `${current}; ${limitsMinMaxLabel}`
        );
    }

    /**
     * Returns the control ID for ui-field (label[for], aria-describedby).
     * Priority: slotted input id > control-id > uid.
     * @returns {string}
     */
    getControlIdForField(): string {
        const controlIdAttr =
            this.getAttribute('control-id') || this.controlId || null;
        const hiddenControl =
            this.control ||
            this.querySelector<HTMLInputElement>(
                'input[type="hidden"].ui-slider__control'
            ) ||
            null;
        if (hiddenControl) {
            const inputId = hiddenControl.getAttribute('id')?.trim();
            if (inputId) {
                return inputId;
            }
            if (controlIdAttr) {
                if (hiddenControl.id !== controlIdAttr) {
                    hiddenControl.id = controlIdAttr;
                    hiddenControl.setAttribute('id', controlIdAttr);
                }
                return controlIdAttr;
            }
            const effectiveId = hiddenControl.id || this.uid;
            if (hiddenControl.id !== effectiveId) {
                hiddenControl.id = effectiveId;
                hiddenControl.setAttribute('id', effectiveId);
            }
            return effectiveId;
        }
        // Hidden control not yet rendered (e.g. ui-field called getControlIdForField during its render).
        // Read slotted input id so label[for] is correct before our render runs.
        const slottedInput = this.queryChildren('input')[0];
        const slottedId = slottedInput?.getAttribute('id')?.trim();
        if (slottedId) {
            return slottedId;
        }
        return controlIdAttr || this.uid;
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _oldValue: AttributeValue,
        newValue: string | null
    ): void {
        if (name !== 'control-id' || this._hasUserSetNativeId) {
            return;
        }
        const hiddenControl = this.control;
        if (!hiddenControl) {
            return;
        }
        if (newValue) {
            hiddenControl.id = newValue;
            hiddenControl.setAttribute('id', newValue);
            if (this.input) {
                this.input.id = newValue;
                this.input.setAttribute('id', newValue);
            }
        } else {
            hiddenControl.removeAttribute('id');
            if (this.input) {
                this.input.removeAttribute('id');
            }
        }
    }

    /**
     * @inheritDoc
     */
    getControlElement(): HTMLInputElement {
        return this.control;
    }

    /**
     * @inheritDoc
     */
    onValuePatched(newValue: AttributeValue): void {
        setNativeInputValue(this.control, String(newValue));
        // calls event dispatch internally in updateValue() -> setValue()
        this.updateValue();
    }

    /**
     * @inheritDoc
     */
    onDisabledStateChanged(newState: boolean): void {
        this.input.disabled = newState;
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        // Set editable attribute by default to all sliders to ensure consistency
        // as specified in UX design requirements.
        this.setAttribute('editable', 'editable');
        const control = this.queryChildren('input')[0] as HTMLInputElement;
        this._hasUserSetNativeId = !!control?.getAttribute('id')?.trim();

        let baseId: string | null = null;
        if (control?.getAttribute('id')?.trim()) {
            baseId = control.getAttribute('id')!.trim();
        } else if (this.getAttribute('control-id') || this.controlId) {
            baseId = this.getAttribute('control-id') || this.controlId || null;
            if (control && baseId) {
                control.id = baseId;
                control.setAttribute('id', baseId);
            }
        } else {
            baseId = this.uid;
            if (control) {
                control.id = baseId;
                control.setAttribute('id', baseId);
            }
        }

        const handleId = `${baseId}-handle`;
        const inputId = `${baseId}-input`;

        this.detachChildNodes();

        const container = createElement<HTMLDivElement>({
            tagName: 'div',
            classList: {
                'ui-slider__container': true,
            },
        })!;

        insertElements(container, [
            {
                tagName: 'input',
                element: control,
                attributes: {
                    type: 'hidden',
                    id: baseId,
                },
                classList: {
                    'ui-slider__control': true,
                },
            },
            {
                tagName: 'div',
                classList: {
                    'ui-slider__bar': true,
                },
                children: [
                    {
                        tagName: 'div',
                        classList: {
                            'ui-slider__track': true,
                        },
                        children: [
                            {
                                tagName: 'div',
                                classList: {
                                    'ui-slider__progress': true,
                                },
                            },
                        ],
                    },
                    {
                        tagName: 'span',
                        attributes: {
                            role: 'slider',
                            tabindex: TabIndex.Active,
                            draggable: 'true',
                            id: handleId,
                        },
                        classList: {
                            'ui-slider__handle': true,
                        },
                    },
                    {
                        tagName: 'div',
                        classList: {
                            'ui-slider__value-container': true,
                            '-hidden': !!this.disableTooltip,
                        },
                        attributes: {
                            role: 'text', // This forces Voiceover to read both value and units together
                        },
                        children: [
                            {
                                tagName: 'div',
                                classList: {
                                    'ui-slider__value': true,
                                },
                            },
                            {
                                tagName: 'div',
                                classList: {
                                    '-visually-hidden': true,
                                },
                                children: [this.units],
                            },
                        ],
                    },
                ],
            },
            {
                tagName: 'div',
                classList: {
                    'ui-slider__limits': true,
                },
                children: [
                    {
                        tagName: 'ui-hint',
                        classList: {
                            'ui-slider__limit': true,
                            '-min': true,
                        },
                    },
                    {
                        tagName: 'ui-hint',
                        classList: {
                            'ui-slider__limit': true,
                            '-max': true,
                        },
                    },
                ],
            },
        ]);

        insertElements(this, [
            container,
            {
                tagName: 'div',
                classList: {
                    'ui-slider__field': true,
                },
                children: [
                    {
                        tagName: 'input',
                        attributes: {
                            'type': 'text',
                            'id': inputId,
                            'aria-label': UISlider.labels.value,
                            'inputmode': 'decimal',
                            'step': control?.getAttribute('step') || null,
                            'min': control?.getAttribute('min') || null,
                            'max': !this.unlimited
                                ? control?.getAttribute('max') || null
                                : null,
                        },
                        classList: {
                            'ui-slider__input': true,
                            '-hidden': !this.editable,
                        },
                    },
                    ...(this.units && this.enableInputUnits
                        ? [
                              {
                                  tagName: 'input',
                                  classList: {
                                      'ui-slider__units-input': true,
                                  },
                                  attributes: {
                                      'type': 'text',
                                      'aria-readonly': 'true',
                                      'aria-label': this.units,
                                      'tabindex': TabIndex.Inactive,
                                      'value': this.units,
                                  },
                              } satisfies ElementConfig,
                          ]
                        : []),
                ],
            },
        ]);

        updateClassList(this, { '-interact': true });
        this.updateHints();
        this.value = Number(this.control.getAttribute('value'));
        this.updateValue();
        updateClassList(this, { '-interact': false });
        this.applyHandleAccessibleName();
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.handleStartMove = this.handleStartMove.bind(this);
        this.handleEndMove = this.handleEndMove.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleDoMove = this.handleDoMove.bind(this);
        this.handleWindowResize = this.handleWindowResize.bind(this);

        window.addEventListener('resize', this.handleWindowResize);

        const barEvents: Record<string, EventListener> = {};
        const keyEvents: Record<string, EventListener> = {
            keydown: this.handleKeydown,
        };

        UISlider.events.start.forEach((key) => {
            keyEvents[key] = this.handleStartMove;
            barEvents[key] = this.handleStartMove;
        });

        updateElement(this.bar, { events: barEvents });
        updateElement(this.handle, {
            events: {
                ...keyEvents,
                focus: (e?: Event) => {
                    this.handleFocusInput.call(this, e as FocusEvent);
                    this.triggerInputValidation();
                },
                input: this.handleInputValue.bind(this.input),
            },
        });
        updateElement(this.input, {
            events: {
                focus: this.handleFocusInput.bind(this),
                blur: this.handleBlurInput.bind(this),
                input: this.handleInputValue.bind(this),
                change: this.handleInputValue.bind(this),
            },
        });
        this.updateInputValue(this.value);
        this.updateMaxlength();
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.sync();
                        this.observer.disconnect();
                    }
                });
            },
            { root: null, rootMargin: '0px' }
        );
        this.observer.observe(this);
        this.controlObserver = new MutationObserver(
            this.handleControlMutations.bind(this)
        );
        this.controlObserver.observe(this.control, { attributes: true });
        runInNextFrame(() => {
            this.updateSliderAccessibility();
        });
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        window.removeEventListener('resize', this.handleWindowResize);
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        window.addEventListener('resize', this.handleWindowResize);
    }
}
